/* eslint-disable no-console */
import { useMemo } from "react";
import {
  ApolloClient,
  ApolloLink,
  from,
  HttpLink,
  InMemoryCache,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import merge from "deepmerge";
import isEqual from "lodash/isEqual";
import configs from "../common/config";
import { GET_REGIONS, purgeRegionsCache } from "../graphql/queries/regions";
import { TYPES_KEYFIELD_MAP } from "../common/constants";
import { getDeviceID } from "./helpers";
import pkg from "../../package.json";
import { getClientCookie } from "./cookiesHelpers";

const noop = () => {};

export const APOLLO_STATE_PROP_NAME = "__APOLLO_STATE__";

let apolloClient;

const {
  CityType: CityTypeKeyFields,
  DirectionType: DirectionTypeKeyFields,
  DistrictType: DistrictTypeKeyFields,
} = TYPES_KEYFIELD_MAP;

const logOperation = (operation) => {
  console.log(`[Operation Name]: ${operation.operationName}`);
  console.log(`[Operation query]: ${JSON.stringify(operation.query)}`);
  console.log(`[Operation variables]: ${JSON.stringify(operation.variables)}`);
};

// FIXME: these logs are temporary to debug prod server. remove them ASAP. 23/07/2022
function createErrorLink(errorCallback) {
  errorCallback = errorCallback || noop;
  return onError(({ networkError, graphQLErrors, operation }) => {
    if (graphQLErrors) {
      console.log(`[Graphql error]: ${JSON.stringify(graphQLErrors)}`);
      logOperation(operation);
    }

    if (networkError) {
      console.log(
        `[Network error]: ${JSON.stringify(networkError)} | ${
          operation.operationName
        }`
      );
      logOperation(operation);
      if (networkError.result?.AuthError === "Invalid Token") {
        errorCallback();
      }
    }
  });
}

const authMiddleware = new ApolloLink((operation, forward) => {
  const { token } = operation.getContext();

  // This will grab the token only on CSR. It is not an issue for now because aqar+ filters are not handled on ssr. 21/05/2023
  const aqarPlusToken = getClientCookie("webapp_token");

  operation.setContext(({ headers = {} }) => ({
    headers: {
      ...headers,
      "req-app": "web",
      "req-device-token": getDeviceID(),
      "app-version": pkg.version,
      // We need to send the token in the header for all public queries for loggedin users
      ...(!!aqarPlusToken && !token && { token: aqarPlusToken }),
    },
    // if we have token in the context we change the ep to be /auth-graphql
    ...(!!token && { uri: `${configs.GQL_AUTH_EP}?token=${token}` }),
  }));

  return forward(operation);
});

const httpLink = new HttpLink({
  uri: configs.GQL_EP,
});

function createApolloClient(errorCallback) {
  return new ApolloClient({
    ssrMode: typeof window === "undefined",
    link: from([createErrorLink(errorCallback), authMiddleware, httpLink]),
    cache: new InMemoryCache({
      typePolicies: {
        Query: {
          fields: {
            Web: {
              merge(existing, incoming, { mergeObjects }) {
                return mergeObjects(existing, incoming);
              },
            },
            Listing: {
              merge(existing, incoming, { mergeObjects }) {
                return mergeObjects(existing, incoming);
              },
            },
          },
        },
        Category: {
          keyFields: ["uri"],
        },
        DistrictType: {
          keyFields: [DistrictTypeKeyFields],
        },
        DirectionType: {
          keyFields: [DirectionTypeKeyFields],
        },
        CityType: {
          keyFields: [CityTypeKeyFields],
        },
        City: {
          keyFields: ["city_id"],
        },
        Direction: {
          keyFields: ["direction_id"],
        },
        District: {
          keyFields: ["district_id"],
        },
        WebListing: {
          keyFields: ["uri"],
        },
      },
    }),
  });
}

export function initApolloClient(initialState = null, errorCallback = noop) {
  const _apolloClient = apolloClient ?? createApolloClient(errorCallback);

  // If we have client side cache and server side cache, merge them together
  if (initialState) {
    const existingCache = _apolloClient.extract();

    const data = merge(initialState, existingCache, {
      arrayMerge: (destinationArray, sourceArray) => [
        ...sourceArray,
        ...destinationArray.filter((d) =>
          sourceArray.every((s) => !isEqual(d, s))
        ),
      ],
    });

    _apolloClient.cache.restore(data);
  }

  // For SSG and SSR always create a new Apollo Client so that data isn't shared between connections
  if (typeof window === "undefined") return _apolloClient;
  // Create the Apollo Client once in the client
  if (!apolloClient) apolloClient = _apolloClient;

  return _apolloClient;
}

export function addApolloState(client, pageProps) {
  const regionsCachedQuery = client.readQuery({ query: GET_REGIONS });
  if (regionsCachedQuery) {
    purgeRegionsCache(client);
  }
  if (pageProps?.props) {
    pageProps.props[APOLLO_STATE_PROP_NAME] = client.cache.extract();
  }

  return pageProps;
}

export function useApollo(pageProps, handleError) {
  const state = pageProps[APOLLO_STATE_PROP_NAME];
  return useMemo(() => initApolloClient(state, handleError), [state]);
}
